import axios from "axios";
import { refreshApi } from "./refreshApi";

const getAccessToken = () => {
  const token = JSON.parse(localStorage.getItem("user"))?.access_token;
  return token;
};

// Partners base URL — env var preferred, hardcoded fallback always works
const PARTNERS_BASE_URL =
  process.env.REACT_APP_PARTNERS_SERVER_URL || "https://partners.tnibro.com";

// =======================
// Create Axios Instances
// =======================
const instance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  headers: {
    Accept: "application/json",
  },
});

export const axiosConfig = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  headers: {
    Accept: "application/json",
  },
});

export const axiosNoToken = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  headers: {
    Accept: "application/json",
  },
});

export const axiosPartners = axios.create({
  baseURL: PARTNERS_BASE_URL,
  headers: {
    Accept: "application/json",
  },
});

// =======================
// Refresh Token Handling
// =======================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// =======================
// Setup Interceptors Function
// =======================
const setupInterceptors = (axiosInstance, instanceName = "axios") => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.log(`[${instanceName}] No token available for this request`);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor - Handle token refresh
  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // 🛑 No response or network error
      if (!error.response) {
        return Promise.reject(error);
      }

      // 🧩 Case 1: Access token expired
      if (error.response.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              console.error(`[${instanceName}] Queue rejected:`, err);
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newAccessToken = await refreshApi();

          // Update localStorage
          const user = JSON.parse(localStorage.getItem("user")) || {};

          user.access_token = newAccessToken;
          localStorage.setItem("user", JSON.stringify(user));

          processQueue(null, newAccessToken);

          // Retry original request with new token

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (err) {
          processQueue(err, null);

          // 🔒 Clear storage and logout
          localStorage.removeItem("user");
          alert("Session expired. Please login again.");
          window.location.href = "/login";
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// =======================
// Apply Interceptors to Both Instances
// =======================
setupInterceptors(instance, "instance");
setupInterceptors(axiosConfig, "axiosConfig");
setupInterceptors(axiosPartners, "axiosPartners");

export default instance;
